import {
	Alert,
	AlertDescription,
	Button,
	Card,
	CardContent,
	CardHeader,
	Input,
	Skeleton,
	TooltipProvider,
} from "@ancatag/at-design";
import { AlertCircle, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import {
	ConfigCard,
	type ConfigCardActions,
	type ConfigCardData,
} from "../components/configs/ConfigCard";
import { type ConnectionStatus } from "../components/configs/ConnectionStatusBadge";
import { type ModelConfig, modelConfigsService } from "../services/models";

// ═══════════════════════════════════════════════════════════════════════════
// MODEL CARD
// ═══════════════════════════════════════════════════════════════════════════

function ModelCard({
	model,
	onTest,
	onDelete,
	onEdit,
}: {
	model: ModelConfig;
	onTest: (id: string) => void;
	onDelete: (id: string) => void;
	onEdit: (id: string) => void;
}) {
	// Map ModelConfig to ConfigCardData
	const cardData: ConfigCardData = {
		id: model.id,
		name: model.name,
		description: model.description,
		generationType: model.generationType,
		isActive: model.isActive,
		connectionStatus: (model.connectionStatus || "unknown") as ConnectionStatus,
		connectionError: model.connectionError,
		providerType: model.providerType,
		modelIdentifier: model.modelIdentifier,
		modelDisplayName: model.modelDisplayName,
		isHosted: model.isHosted,
	};

	const cardActions: ConfigCardActions = {
		onEdit: () => onEdit(model.id),
		onTest: async () => onTest(model.id),
		onDelete: async () => onDelete(model.id),
	};

	return <ConfigCard data={cardData} actions={cardActions} type="model" />;
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function UserModels() {
	const [, navigate] = useLocation();
	const [models, setModels] = useState<ModelConfig[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [generationType, setGenerationType] = useState<string>("all");
	const [providerType, setProviderType] = useState<string>("all");

	const fetchData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);
			const params: any = { limit: 50 };
			if (search) params.search = search;
			if (generationType !== "all") params.generationType = generationType;
			if (providerType !== "all") params.providerType = providerType;

			const response = await modelConfigsService.list(params);
			setModels(response.data || []);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load models");
		} finally {
			setLoading(false);
		}
	}, [search, generationType, providerType]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleEdit = (modelId: string) => {
		navigate(`/models/manage/${modelId}/edit`);
	};

	const handleTest = async (modelId: string) => {
		try {
			const result = await modelConfigsService.testConnection(modelId);
			if (result.success) {
				toast.success(`Connection test successful! (${result.responseTime}ms)`);
			} else {
				toast.error(
					`Connection test failed: ${result.error || "Unknown error"}`,
				);
			}
			fetchData(); // Refresh to show updated status
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Connection test failed",
			);
		}
	};

	const handleDelete = async (modelId: string) => {
		if (!confirm(`Delete this model? This action cannot be undone.`)) return;
		try {
			await modelConfigsService.delete(modelId);
			toast.success("Model deleted");
			fetchData();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Delete failed");
		}
	};

	return (
		<TooltipProvider>
			<div className="container mx-auto max-w-7xl py-8 px-4 sm:px-6 lg:px-8">
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
					<div>
						<h1 className="text-2xl md:text-3xl font-bold text-foreground">
							Model Connections
						</h1>
						<p className="text-muted-foreground mt-2 text-sm md:text-base">
							Manage your AI provider connections. Create connections to AI
							providers (like Ollama, OpenAI) and use them in route
							configurations.
						</p>
					</div>
					<Button
						onClick={() => navigate("/models/manage/new")}
						className="w-full sm:w-auto"
					>
						<Plus className="h-4 w-4 mr-2" />
						New Model Connection
					</Button>
				</div>

				{error && (
					<Alert variant="destructive" className="mb-6">
						<AlertCircle className="h-4 w-4" />
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}

				{/* Search and Filters */}
				<div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
					<Input
						placeholder="Search models..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="max-w-sm"
					/>
					<select
						value={generationType}
						onChange={(e) => setGenerationType(e.target.value)}
						className="px-3 py-2 border border-input bg-background rounded-md text-sm"
					>
						<option value="all">All Types</option>
						<option value="text">Text</option>
						<option value="image">Image</option>
						<option value="video">Video</option>
						<option value="audio">Audio</option>
					</select>
					<select
						value={providerType}
						onChange={(e) => setProviderType(e.target.value)}
						className="px-3 py-2 border border-input bg-background rounded-md text-sm"
					>
						<option value="all">All Providers</option>
						<option value="ollama">Ollama</option>
						<option value="openai">OpenAI</option>
						<option value="anthropic">Anthropic</option>
					</select>
				</div>

				{/* Models Grid */}
				{loading ? (
					<div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						{[...Array(6)].map((_, i) => (
							<Card key={i}>
								<CardHeader className="pb-3">
									<Skeleton className="h-5 w-32 mb-2" />
									<Skeleton className="h-4 w-full" />
								</CardHeader>
								<CardContent>
									<Skeleton className="h-20 w-full" />
								</CardContent>
							</Card>
						))}
					</div>
				) : !models || models.length === 0 ? (
					<div className="p-8 border-2 border-dashed border-border rounded-lg text-center space-y-3">
						<p className="text-muted-foreground">
							{search
								? "No model connections match your search."
								: "No model connections yet. Create your first one."}
						</p>
					</div>
				) : (
					<div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
						{models.map((model) => (
							<ModelCard
								key={model.id}
								model={model}
								onTest={handleTest}
								onDelete={handleDelete}
								onEdit={handleEdit}
							/>
						))}
					</div>
				)}
			</div>
		</TooltipProvider>
	);
}
